import React,{useState, useEffect, useMemo} from "react";
import axios from "axios";
import { X } from "lucide-react";

const EditProductModal = ({ open, onClose, productId, initialName = "", product,  onSuccess, onFailed }) => {
    const [hierarchy, setHierarchy] = useState([]);
    const [loadingHierarchy, setLoadingHierarchy] = useState(false);
    const [form, setForm] = useState({ productName: initialName, parentDomainId: "", domainId: "", subDomainId: "" });
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
  
    useEffect(() => {
      if (open) {
        setForm({ productName: initialName, parentDomainId: product?.parentDomainId, domainId: product?.domainId, subDomainId: product?.subDomainId });
        setErrors({});
      }
    }, [open, initialName]);
  
    useEffect(() => {
        let isMounted = true;
        const fetchHierarchy = async () => {
          setLoadingHierarchy(true);
          try {
            const response = await axios.get(
              `${process.env.REACT_APP_API_URL}/parentdomain_domain_subdomain_list/`
            );
            if (isMounted) {
              setHierarchy(response.data?.parent_domains || []);
            }
          } catch (error) {
            console.error("Failed to load domain hierarchy", error);
          } finally {
            if (isMounted) {
              setLoadingHierarchy(false);
            }
          }
        };
    
        fetchHierarchy();
        return () => {
          isMounted = false;
        };
      }, []);
  
    const parentOptions = useMemo(
      () => hierarchy.map((p) => ({ id: p.parent_domain_id, label: p.parent_domain, domains: p.domains || [] })),
      [hierarchy]
    );
  
    const domainOptions = useMemo(() => {
      const all = parentOptions.flatMap((p) =>
        p.domains.map((d) => ({ id: d.domain_id, label: d.domain, parentId: p.id, subDomains: d.sub_domains || [] }))
      );
      return form.parentDomainId ? all.filter((d) => d.parentId === form.parentDomainId) : all;
    }, [parentOptions, form.parentDomainId]);
  
    const subDomainOptions = useMemo(() => {
      const all = domainOptions.flatMap((d) =>
        d.subDomains.map((s) => ({ id: s.sub_domain_id, label: s.sub_domain, domainId: d.id, parentId: d.parentId }))
      );
      if (form.domainId) return all.filter((s) => s.domainId === form.domainId);
      if (form.parentDomainId) return all.filter((s) => s.parentId === form.parentDomainId);
      return all;
    }, [domainOptions, form.domainId, form.parentDomainId]);
  
    useEffect(() => {
      if (form.parentDomainId && domainOptions.length === 1 && !form.domainId)
        setForm((f) => ({ ...f, domainId: domainOptions[0].id }));
    }, [domainOptions, form.parentDomainId, form.domainId]);
  
    useEffect(() => {
      if (form.domainId && subDomainOptions.length === 1 && !form.subDomainId)
        setForm((f) => ({ ...f, subDomainId: subDomainOptions[0].id }));
    }, [subDomainOptions, form.domainId, form.subDomainId]);
  
    const handleParentChange = (e) => {
      setForm((f) => ({ ...f, parentDomainId: e.target.value, domainId: "", subDomainId: "" }));
      setErrors((er) => ({ ...er, parentDomainId: "" }));
    };
    const handleDomainChange = (e) => {
      const id = e.target.value;
      const domain = domainOptions.find((d) => d.id === id);
      setForm((f) => ({ ...f, domainId: id, subDomainId: "", parentDomainId: domain?.parentId || f.parentDomainId }));
      setErrors((er) => ({ ...er, domainId: "" }));
    };
    const handleSubDomainChange = (e) => {
      const id = e.target.value;
      const sub = subDomainOptions.find((s) => s.id === id);
      setForm((f) => ({ ...f, subDomainId: id, domainId: sub?.domainId || f.domainId, parentDomainId: sub?.parentId || f.parentDomainId }));
      setErrors((er) => ({ ...er, subDomainId: "" }));
    };
  
    const allFilled = form.productName.trim() && form.parentDomainId && form.domainId && form.subDomainId;
   
  
    const handleSubmit = async () => {
      const e = {};
      if (!form.productName.trim()) e.productName = "Product name is required.";
      if (!form.parentDomainId) e.parentDomainId = "Please select a parent domain.";
      if (!form.domainId) e.domainId = "Please select a domain.";
      if (!form.subDomainId) e.subDomainId = "Please select a sub domain.";
      if (Object.keys(e).length) { setErrors(e); return; }
  
      setSubmitting(true);
      try {
        const payload = {
          product_name: form.productName.trim(),
          parent_domain_id: form.parentDomainId,
          domain_id: form.domainId,
          sub_domain_id: form.subDomainId,
        };
        const {data} = await axios.put(`${process.env.REACT_APP_API_URL}/update-product/${productId}`, payload);
        onSuccess?.(data);
        onClose();
      } catch {
        setErrors({ submit: "Something went wrong. Please try again." });
        onFailed?.()
      } finally {
        setSubmitting(false);
      }
    };
  
    if (!open) return null;
  
    const inputBase = "w-full px-3 py-2.5 text-sm rounded-lg border bg-gray-50 text-gray-800 outline-none transition-all duration-150 focus:ring-2 focus:ring-violet-500 focus:border-violet-500 focus:bg-white appearance-none";
    const inputNormal = "border-gray-200";
    const inputErr    = "border-red-400 focus:ring-red-400 focus:border-red-400";
    const inputDis    = "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200 pointer-events-none";
    const labelCls    = "block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5";
    const errorCls    = "mt-1 text-xs text-red-500 flex items-center gap-1";
    const selCls      = (hasErr, dis) => `${inputBase} pr-8 ${dis ? inputDis : hasErr ? inputErr : inputNormal}`;
  
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        style={{ background: "rgba(60,50,120,0.18)", backdropFilter: "blur(4px)" }}
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      >
        <div
          className="w-[500px]  bg-white rounded-2xl shadow-2xl overflow-hidden"
          style={{ animation: "epIn .22s cubic-bezier(.34,1.56,.64,1)" }}
        >
          {/* Header */}
          <div
            className="flex items-center justify-between px-6 py-5"
            style={{ background: "linear-gradient(135deg,#6C5CE7 0%,#8e7ff0 100%)" }}
          >
            <div>
              <h2 className="text-white font-bold text-lg tracking-tight">Edit Product</h2>
              <p className="text-violet-200 text-xs mt-0.5">Update product details below</p>
            </div>
            <button
              onClick={onClose}
              className=" h-8 rounded-lg flex items-center justify-center text-white text-sm hover:opacity-70 transition-opacity"
              
            ><X className="h-5" /></button>
          </div>
  
          {/* Body */}
          <div className="px-6 pt-6 pb-4 flex flex-col gap-5">
  
            {/* Product Name */}
            <div>
              <label className={labelCls}>Product Name <span className="text-red-400">*</span></label>
              <input
                className={`${inputBase} ${errors.productName ? inputErr : inputNormal}`}
                placeholder="Enter product name"
                value={form.productName}
                onChange={(e) => { setForm((f) => ({ ...f, productName: e.target.value })); setErrors((er) => ({ ...er, productName: "" })); }}
              />
              {errors.productName && <p className={errorCls}><span>⚠</span>{errors.productName}</p>}
            </div>
  
            {/* Parent Domain */}
            <div>
              <label className={labelCls}>Parent Domain <span className="text-red-400">*</span></label>
              <div className="relative">
                <select className={selCls(!!errors.parentDomainId, loadingHierarchy)} value={form.parentDomainId} onChange={handleParentChange} disabled={loadingHierarchy}>
                  <option value="">{loadingHierarchy ? "Loading…" : "Select parent domain"}</option>
                  {parentOptions.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                </select>
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-violet-500 text-xs">▼</span>
              </div>
              {errors.parentDomainId && <p className={errorCls}><span>⚠</span>{errors.parentDomainId}</p>}
            </div>
  
            {/* Domain */}
            <div>
              <label className={labelCls}>Domain <span className="text-red-400">*</span></label>
              <div className="relative">
                <select className={selCls(!!errors.domainId, loadingHierarchy || !domainOptions.length)} value={form.domainId} onChange={handleDomainChange} disabled={loadingHierarchy || !domainOptions.length}>
                  <option value="">
                    {loadingHierarchy ? "Loading…" : form.parentDomainId && !domainOptions.length ? "No domains available" : "Select domain"}
                  </option>
                  {domainOptions.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                </select>
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-violet-500 text-xs">▼</span>
              </div>
              {errors.domainId && <p className={errorCls}><span>⚠</span>{errors.domainId}</p>}
            </div>
  
            {/* Sub Domain */}
            <div>
              <label className={labelCls}>Sub Domain <span className="text-red-400">*</span></label>
              <div className="relative">
                <select className={selCls(!!errors.subDomainId, loadingHierarchy || !subDomainOptions.length)} value={form.subDomainId} onChange={handleSubDomainChange} disabled={loadingHierarchy || !subDomainOptions.length}>
                  <option value="">
                    {loadingHierarchy ? "Loading…" : form.domainId && !subDomainOptions.length ? "No sub domains available" : "Select sub domain"}
                  </option>
                  {subDomainOptions.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                </select>
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-violet-500 text-xs">▼</span>
              </div>
              {errors.subDomainId && <p className={errorCls}><span>⚠</span>{errors.subDomainId}</p>}
            </div>
  
            {errors.submit && <p className="text-center text-sm text-red-500">⚠ {errors.submit}</p>}
          </div>
  
          {/* Footer */}
          <div className="px-6 pb-6 flex justify-end gap-3">
            <button onClick={onClose} className="px-5 py-2.5 rounded-lg text-sm font-semibold text-gray-500 border border-gray-200 hover:bg-violet-50 hover:text-violet-600 hover:border-violet-200 transition-all duration-150">
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!allFilled || submitting}
              className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-150 flex items-center gap-2 ${allFilled && !submitting ? "bg-gradient-to-br from-[#6C5CE7] to-[#8e7ff0] text-white shadow-sm shadow-[#8e7ff0] hover:-translate-y-0.5" : "bg-gray-100 text-gray-400 cursor-not-allowed"}`}
            >
              {submitting
                ? <><svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>Saving…</>
                : "Save Changes"}
            </button>
          </div>
        </div>
        <style>{`@keyframes epIn{from{opacity:0;transform:scale(.93) translateY(14px)}to{opacity:1;transform:scale(1) translateY(0)}}`}</style>
      </div>
    );
  };

  export default EditProductModal;